/**
 * How /audience words what it shows (spec §3.4, §4.4, §5, §6): the Active filter, the empty line, the filters and
 * the footnotes. The page and the CSV share every word here, so the file says what the page says. Every word is a
 * literal t('…') key, so the locale integrity test sees it; channel names come from D3's channelFilterText (the one
 * file of platform names). null is "—", never 0. Pure.
 *
 * @module services/audience/audienceText
 */
import { isFiltering } from '@/services/channelFilter';
import type { TFn } from '@/services/channels/channelFormat';
import { channelFilterText } from '@/services/creatives/creativeText';
import { objectiveText } from '@/services/overview/overviewFormat';
import type { AudienceFilters, AudienceTableData } from './audienceTypes';

/** The filters an empty table names (R9). */
export type AppliedFilters = Pick<AudienceFilters, 'channels' | 'objective' | 'activeOnly'>;

/**
 * The Active filter as the print line and the CSV say it (R12, R13): off, "ทุก campaign"; on, "เฉพาะ campaign ที่
 * Active (ซ่อน N)" while it hides some, else D2's words (none hidden, or not yet known).
 */
export const activeFilterText = (activeOnly: boolean, hidden: number | null, t: TFn): string => {
  if (!activeOnly) return t('audience.activeOff');
  return hidden !== null && hidden > 0 ? t('audience.activeOnHidden', { n: hidden }) : t('channels.breakdown.activeOnly');
};

/** The filters that apply, in the band's order: "Facebook, TikTok · Conversion · เฉพาะ campaign ที่ Active". */
export const filterWordsOf = (f: AppliedFilters, t: TFn): string => [
  isFiltering(new Set(f.channels)) ? channelFilterText(f.channels, t) : '',
  f.objective === 'all' ? '' : objectiveText(f.objective, t),
  f.activeOnly ? t('channels.breakdown.activeOnly') : '',
].filter((w) => w !== '').join(' · ');

/** R9: no ad set in the page currency → "ไม่มี ad set ในช่วงนี้ · <scope>"; else the filters emptied it, and are named. */
export const emptyTextOf = (data: Pick<AudienceTableData, 'inCurrency'>, scope: string, f: AppliedFilters, t: TFn): string =>
  (data.inCurrency === 0
    ? t('audience.empty', { scope })
    : t('audience.emptyFilters', { scope, filters: filterWordsOf(f, t) }));

/** Spec §4.2's rule, then " · ช่วงนี้ตัดออก N ad group" when it left some out (R8); none while the facts load. */
export const searchShoppingNote = (leftOut: number | null, t: TFn): string => {
  const rule = t('audience.notes.searchShopping');
  return leftOut !== null && leftOut > 0 ? `${rule} · ${t('audience.notes.leftOut', { n: leftOut })}` : rule;
};

/** The two rules under the table, on the page and at the end of the file's section 2 (R12): n<500, the segment. */
export const ruleNotesOf = (t: TFn): string[] => [t('audience.notes.lowSample'), t('audience.notes.segments')];

export interface FootnoteInput {
  /** The Google ad groups §4.2 left out; null until the facts are ready. */
  leftOut: number | null;
  /** Ad sets dropped because their account's currency is unknown (spec §4.3). */
  unknownCurrency: number;
}

/** The two notes about the data: Search and Shopping with its count, the unknown currency. The empty card keeps just these (rules-04 A3). */
export const dataNotesOf = (n: FootnoteInput, t: TFn): string[] => [
  searchShoppingNote(n.leftOut, t),
  ...(n.unknownCurrency > 0 ? [t('audience.notes.unknownCurrency', { n: n.unknownCurrency })] : []),
];

/** Box 2's footnotes, in spec §3.4's order: the two rules, then the notes about the data. */
export const footnotesOf = (n: FootnoteInput, t: TFn): string[] => [...ruleNotesOf(t), ...dataNotesOf(n, t)];
